import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "../components/Icon";
import { PageTitle } from "../components/PageTitle";
import { Button, EmptyState, Field, Input, Select } from "../components/ui";
import { FamilyClassifyToolbar } from "../components/FamilyClassifyToolbar";
import { LeadBulkToolbar } from "../components/LeadBulkToolbar";
import { ProjectDetailView, type ProjectDetail } from "../components/ProjectDetailView";
import { useToast } from "../components/Toast";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { ApiError, apiGet, getFamilies, leadProjectSummaries, mergeProjects } from "../lib/api";
import { downloadProjectsCsv } from "../lib/projectCsvExport";
import { fieldValue, normalizePdrProfile, pdrProfileDisplayLabel } from "../lib/pdrFields";
import { discoverySourceTagClass } from "../lib/discoverySources";
import type { ProjectData } from "../lib/pdrFields";
import type { FamilyOverview, LeadProjectSummary } from "../lib/types";
import {
  LEAD_FILTER_OPTIONS,
  hasGeneratedLeads,
  isFoundViaLead,
  matchesLeadFilter,
  type LeadFilter,
} from "../lib/leads";
import { formatApiError } from "../lib/utils";

const DUPLICATION = "DUPLICATION";

function familyOf(p: { project_data?: ProjectData }): { name: string; id: string; duplicate: boolean } {
  const name = fieldValue(p.project_data || {}, "project_family").trim();
  const id = fieldValue(p.project_data || {}, "project_family_id").trim();
  return { name, id, duplicate: name.toUpperCase() === DUPLICATION };
}

type ProjectRow = {
  project_id: string;
  project_slug: string;
  project_name: string;
  target_company: string;
  vendor_name: string;
  last_updated_at: string;
  pdr_reasoning_enabled?: boolean;
  pdr_schema_profile?: string;
  discovery_sources?: string[];
  status?: string;
  project_data?: ProjectData;
};

export function ProjectsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const projectParam = params.get("project") || "";

  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [total, setTotal] = useState(0);
  const [target, setTarget] = useState("");
  const debouncedTarget = useDebouncedValue(target, 400);
  const [listFilter, setListFilter] = useState("");
  // Narrow the list (and so "Select all shown") by lead coverage.
  const [leadFilter, setLeadFilter] = useState<LeadFilter>("all");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(projectParam);
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [leadSummaries, setLeadSummaries] = useState<Record<string, LeadProjectSummary>>({});
  const [detailRefresh, setDetailRefresh] = useState(0);
  // Project families (plans/project-families.md): the bank's families as stored.
  const [familyOverview, setFamilyOverview] = useState<FamilyOverview | null>(null);
  const [hideDuplicates, setHideDuplicates] = useState(false);
  const [groupByFamily, setGroupByFamily] = useState(false);
  const [merging, setMerging] = useState("");

  // `targetOverride` lets the "Apply" button search immediately with the
  // current input instead of waiting for the debounce timer to catch up.
  const loadList = useCallback(
    async (targetOverride?: string) => {
      setLoading(true);
      try {
        const q = new URLSearchParams();
        const t = (targetOverride ?? debouncedTarget).trim();
        if (t) q.set("target", t);
        const data = await apiGet<{ projects: ProjectRow[]; total: number }>(
          `/api/projects?${q}`,
        );
        setProjects(data.projects || []);
        setTotal(data.total ?? 0);
      } catch (e) {
        toast(formatApiError(e), true);
      } finally {
        setLoading(false);
      }
    },
    [debouncedTarget, toast],
  );

  const loadDetail = useCallback(
    async (projectId: string) => {
      if (!projectId) {
        setDetail(null);
        return;
      }
      setDetailLoading(true);
      const fromList = projects.find((p) => p.project_id === projectId);
      const listFallback = (): ProjectDetail | null => {
        if (!fromList?.project_data) return null;
        return {
          project_id: fromList.project_id,
          project_slug: fromList.project_slug,
          project_name: fromList.project_name,
          target_company: fromList.target_company,
          vendor_name: fromList.vendor_name,
          last_updated_at: fromList.last_updated_at,
          pdr_reasoning_enabled: fromList.pdr_reasoning_enabled,
          pdr_schema_profile: fromList.pdr_schema_profile,
          status: fromList.status,
          project_data: fromList.project_data,
          lineage: [],
        };
      };
      try {
        const q = new URLSearchParams({ project_id: projectId });
        const data = await apiGet<{ project?: ProjectDetail; projects?: ProjectDetail[] }>(
          `/api/projects?${q}`,
        );
        const project = data.project || data.projects?.[0];
        if (project) {
          setDetail(project);
          return;
        }
        const fallback = listFallback();
        if (fallback) {
          setDetail(fallback);
          return;
        }
        setDetail(null);
        toast("Project not found", true);
      } catch (e) {
        const fallback = listFallback();
        if (fallback && e instanceof ApiError && (e.status === 404 || e.status === 422)) {
          setDetail(fallback);
          return;
        }
        setDetail(null);
        toast(formatApiError(e), true);
      } finally {
        setDetailLoading(false);
      }
    },
    [projects, toast],
  );

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (projectParam) setSelectedId(projectParam);
  }, [projectParam]);

  useEffect(() => {
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const loadLeadSummaries = useCallback(async (rows: ProjectRow[]) => {
    const ids = rows.map((p) => p.project_id).filter(Boolean);
    if (!ids.length) {
      setLeadSummaries({});
      return;
    }
    try {
      const data = await leadProjectSummaries(ids);
      setLeadSummaries(data.summaries || {});
    } catch {
      /* badges are a convenience; the list stays usable without them */
    }
  }, []);

  useEffect(() => {
    void loadLeadSummaries(projects);
  }, [projects, loadLeadSummaries]);

  // Families are per bank: the pass and its overview need the list to show exactly one.
  const bankName = useMemo(() => {
    if (!debouncedTarget.trim() || !projects.length) return "";
    const names = new Set(projects.map((p) => p.target_company).filter(Boolean));
    return names.size === 1 ? [...names][0] : "";
  }, [debouncedTarget, projects]);

  const loadFamilyOverview = useCallback(async () => {
    if (!bankName) {
      setFamilyOverview(null);
      return;
    }
    try {
      setFamilyOverview(await getFamilies(bankName));
    } catch {
      /* the family view is a convenience; the list stays usable without it */
      setFamilyOverview(null);
    }
  }, [bankName]);

  useEffect(() => {
    void loadFamilyOverview();
  }, [loadFamilyOverview]);

  const duplicateOf = useMemo(() => {
    const map: Record<string, { id: string; name: string }> = {};
    for (const d of familyOverview?.duplicates || []) {
      map[d.project_id] = { id: d.duplicate_of_id, name: d.duplicate_of_name };
    }
    return map;
  }, [familyOverview]);

  const textFiltered = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => {
      const deal = fieldValue(p.project_data || {}, "deal_score");
      const hay = `${p.project_name} ${p.target_company} ${p.vendor_name} ${p.project_slug} ${deal}`.toLowerCase();
      return hay.includes(q);
    });
  }, [projects, listFilter]);

  const filtered = useMemo(
    () =>
      textFiltered.filter(
        (p) =>
          matchesLeadFilter(leadSummaries[p.project_id], leadFilter) && !(hideDuplicates && familyOf(p).duplicate),
      ),
    [textFiltered, leadSummaries, leadFilter, hideDuplicates],
  );

  // Group by family ID (duplicates carry the ID of the project they repeat); projects the
  // pass has not placed yet come last.
  const familyGroups = useMemo(() => {
    if (!groupByFamily) return null;
    const groups = new Map<string, { key: string; label: string; rows: ProjectRow[] }>();
    for (const p of filtered) {
      const family = familyOf(p);
      const key = family.id || (family.name && !family.duplicate ? `name:${family.name}` : "");
      const label = key
        ? `${family.id ? `${family.id} · ` : ""}${family.duplicate ? "" : family.name}`
        : "No family yet";
      const group = groups.get(key) || { key, label, rows: [] };
      if (!family.duplicate && family.name && !group.label.includes(family.name)) group.label = `${family.id} · ${family.name}`;
      group.rows.push(p);
      groups.set(key, group);
    }
    return [...groups.values()].sort((a, b) =>
      !a.key ? 1 : !b.key ? -1 : a.key.localeCompare(b.key, undefined, { numeric: true }),
    );
  }, [filtered, groupByFamily]);

  const leadFilterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const option of LEAD_FILTER_OPTIONS) {
      counts[option.id] = textFiltered.filter((p) => matchesLeadFilter(leadSummaries[p.project_id], option.id)).length;
    }
    return counts;
  }, [textFiltered, leadSummaries]);

  const selectProject = useCallback(
    (projectId: string) => {
      setSelectedId(projectId);
      const next = new URLSearchParams(params);
      if (projectId) next.set("project", projectId);
      else next.delete("project");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // A merge, split or revert in the detail view changes stored identity: the
  // list can gain, lose or rename a row, so both reads are redone rather than
  // patched in place.
  const handleProjectChanged = useCallback(() => {
    void loadList();
    void loadDetail(selectedId);
  }, [loadList, loadDetail, selectedId]);

  // A project found via another project's lead research is never a lead source,
  // so it cannot be ticked for lead work at all.
  const visibleIds = useMemo(
    () => filtered.filter((p) => !isFoundViaLead(leadSummaries[p.project_id])).map((p) => p.project_id),
    [filtered, leadSummaries],
  );
  // Only ticked projects the filters show are acted on: one ticked earlier and
  // then filtered out (say, because it now has leads) must not ride along.
  const selectedList = useMemo(
    () =>
      filtered
        .filter((p) => selectedIds.has(p.project_id) && !isFoundViaLead(leadSummaries[p.project_id]))
        .map((p) => p.project_id),
    [selectedIds, filtered],
  );
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  const toggleSelected = (projectId: string, on: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(projectId);
      else next.delete(projectId);
      return next;
    });
  };

  const toggleAllVisible = (on: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of visibleIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const mergeDuplicate = async (duplicateId: string, duplicateName: string) => {
    const kept = duplicateOf[duplicateId];
    if (!kept?.id) return;
    const ok = window.confirm(
      `Merge "${duplicateName}" into "${kept.name}"? The duplicate is absorbed: its mentions and lineage move to "${kept.name}". The merge can be undone.`,
    );
    if (!ok) return;
    setMerging(duplicateId);
    try {
      await mergeProjects({ survivor_project_id: kept.id, absorbed_project_id: duplicateId });
      toast(`Merged into ${kept.name}.`);
      void loadList();
      void loadFamilyOverview();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setMerging("");
    }
  };

  const handleFamiliesChanged = useCallback(() => {
    void loadList();
    void loadFamilyOverview();
    setDetailRefresh((v) => v + 1);
  }, [loadList, loadFamilyOverview]);

  // Bulk lead generation or planning changed leads on many projects: redo the
  // badges and the open detail view's lead panel.
  const handleLeadsChanged = useCallback(() => {
    void loadLeadSummaries(projects);
    setDetailRefresh((v) => v + 1);
  }, [loadLeadSummaries, projects]);

  const companyForExport = target.trim() || detail?.target_company || "";

  const openTableView = () => {
    navigate(`/projects/export?target=${encodeURIComponent(companyForExport)}`);
  };

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const { filename, savedPath } = await downloadProjectsCsv(target);
      const savedNote = savedPath ? ` Saved to ${savedPath}.` : "";
      toast(`Downloaded ${filename}.${savedNote}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : "CSV export failed", true);
    } finally {
      setExportingCsv(false);
    }
  };

  const renderRow = (p: ProjectRow) => {
    const active = selectedId === p.project_id;
    const family = familyOf(p);
    const kept = duplicateOf[p.project_id];
    const deal = fieldValue(p.project_data || {}, "deal_score");
    const risk = fieldValue(p.project_data || {}, "project_risk_level");
    const reasoningOn = p.pdr_reasoning_enabled === true;
    const pdrProfileLabel = pdrProfileDisplayLabel(p.pdr_schema_profile);
    const pdrComplete =
      normalizePdrProfile(p.pdr_schema_profile) === "complete";
    const leads = leadSummaries[p.project_id];
    return (
      <div
        key={p.project_id}
        className={`flex items-start gap-2 border-b border-outline-variant ${
          active
            ? "border-l-4 border-l-primary bg-surface-container-high"
            : "hover:bg-surface-container border-l-4 border-l-transparent"
        }`}
      >
      <input
        type="checkbox"
        className="mt-5 ml-3 shrink-0"
        checked={selectedIds.has(p.project_id) && !isFoundViaLead(leads)}
        disabled={isFoundViaLead(leads)}
        onChange={(e) => toggleSelected(p.project_id, e.target.checked)}
        aria-label={`Select ${p.project_name}`}
        title={
          isFoundViaLead(leads)
            ? "Found via lead research: leads are not generated for it (no second round)"
            : undefined
        }
      />
      <button
        type="button"
        onClick={() => selectProject(p.project_id)}
        className="flex-1 min-w-0 text-left py-4 pr-4"
      >
        <p className="text-body-sm font-semibold text-on-surface truncate">{p.project_name}</p>
        <p className="text-[10px] font-data-mono text-primary mt-0.5 truncate">
          {p.target_company}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {family.duplicate ? (
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase border-error/40 text-error"
              title="Marked DUPLICATION by the family pass: kept for traceability, left out of the export"
            >
              Duplicate{kept?.name ? ` of ${kept.name}` : ""}
            </span>
          ) : family.id || family.name ? (
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded border bg-secondary/10 text-secondary border-secondary/20 max-w-full truncate"
              title={`Project family: ${family.name || "—"}${family.id ? ` (${family.id})` : ""}`}
            >
              {family.id ? `${family.id} · ` : ""}
              {family.name}
            </span>
          ) : null}
          {p.vendor_name ? (
            <span className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-primary/10 text-primary border-primary/20">
              {p.vendor_name}
            </span>
          ) : null}
          {reasoningOn ? (
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-tertiary/10 text-tertiary border-tertiary/20"
              title="Synthesized with audit reasoning (value + rationale per field)"
            >
              Reasoning on
            </span>
          ) : null}
          <span
            className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase ${
              pdrComplete
                ? "bg-primary/10 text-primary border-primary/20"
                : "bg-surface-container-high text-on-surface-variant border-outline-variant"
            }`}
            title={`Synthesized with ${pdrProfileLabel} schema`}
          >
            {pdrProfileLabel}
          </span>
          {(p.discovery_sources || []).map((src) => (
            <span
              key={src}
              className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase ${discoverySourceTagClass(src)}`}
              title={`Evidence from ${src}`}
            >
              {src}
            </span>
          ))}
          {deal ? (
            <span className="text-[9px] font-bold px-2 py-0.5 rounded border bg-secondary/10 text-secondary border-secondary/20 uppercase">
              Deal {deal}
            </span>
          ) : null}
          {risk ? (
            <span className="text-[9px] font-bold px-2 py-0.5 rounded border border-outline-variant text-on-surface-variant uppercase">
              {risk} risk
            </span>
          ) : null}
          {leads?.open_leads ? (
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-tertiary/10 text-tertiary border-tertiary/20"
              title={`${leads.open_leads} open possible next projects${leads.researching ? `, ${leads.researching} queued for research` : ""}`}
            >
              {leads.open_leads} lead{leads.open_leads === 1 ? "" : "s"}
            </span>
          ) : null}
          {leads?.found_projects ? (
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-primary/10 text-primary border-primary/20"
              title="Projects found by this project's lead research"
            >
              {leads.found_projects} found
            </span>
          ) : null}
          {isFoundViaLead(leads) ? (
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-tertiary/10 text-tertiary border-tertiary/30"
              title={`Found by the research of lead "${leads?.origin_lead || "?"}"${leads?.origin_project_name ? ` from ${leads.origin_project_name}` : ""}. Leads are not generated for it.`}
            >
              found via lead
            </span>
          ) : leads && !hasGeneratedLeads(leads) ? (
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase border-secondary/40 text-secondary"
              title={
                leads.events
                  ? `No leads generated yet (${leads.events} roadmap event${leads.events === 1 ? "" : "s"})`
                  : "No leads generated yet; they will come from the project record"
              }
            >
              no leads yet
            </span>
          ) : leads?.stale ? (
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase border-secondary/40 text-secondary"
              title="A roadmap event changed or arrived after the leads were generated"
            >
              new events
            </span>
          ) : null}
          {leads?.last_error ? (
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase border-error/40 text-error"
              title={leads.last_error}
            >
              leads failed
            </span>
          ) : null}
        </div>
        <p className="text-[10px] text-on-surface-variant font-data-mono mt-2">
          {p.last_updated_at
            ? new Date(p.last_updated_at).toLocaleString()
            : "—"}
        </p>
      </button>
      {family.duplicate && kept?.id ? (
        <Button
          variant="link-muted"
          className="mt-4 mr-3 shrink-0"
          disabled={merging === p.project_id}
          onClick={() => void mergeDuplicate(p.project_id, p.project_name)}
          title={`Merge this duplicate into ${kept.name} (undoable)`}
        >
          {merging === p.project_id ? "Merging…" : "Merge"}
        </Button>
      ) : null}
      </div>
    );
  };

  return (
    <main className="flex-1 flex flex-col h-full overflow-hidden min-w-0 bg-background">
      <div className="shrink-0 border-b border-outline-variant bg-surface-container">
        <PageTitle
          phaseNum={4}
          phaseLabel="Projects"
          title="Canonical projects"
          meta="Full PDR intelligence with audit reasoning per inferred field"
        />
        <div className="px-6 pb-4 flex flex-wrap gap-3 items-end border-t border-outline-variant/40 pt-3">
          <Field label="Target filter" className="min-w-[200px]">
            <Input
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="e.g. Worldline AS"
            />
          </Field>
          <Button variant="primary" onClick={() => loadList(target)}>
            Apply
          </Button>
          <Button
            variant="primary"
            onClick={openTableView}
            disabled={!companyForExport}
            title={
              companyForExport
                ? `View all ${companyForExport} projects as a table`
                : "Filter by a target company (or select a project) to enable the table view"
            }
          >
            <Icon name="table_view" className="text-base" />
            Table View
          </Button>
          <Button
            variant="secondary"
            onClick={() => void exportCsv()}
            disabled={!projects.length || exportingCsv}
          >
            {exportingCsv ? "Exporting…" : "Export CSV"}
          </Button>
          <span className="text-body-sm text-on-surface-variant ml-auto">
            {total} project{total === 1 ? "" : "s"}
          </span>
        </div>
        <LeadBulkToolbar
          projectIds={selectedList}
          selectionCount={selectedList.length}
          visibleCount={visibleIds.length}
          allVisibleSelected={allVisibleSelected}
          onToggleAll={toggleAllVisible}
          onChanged={handleLeadsChanged}
        />
        {bankName ? (
          <FamilyClassifyToolbar target={bankName} overview={familyOverview} onChanged={handleFamiliesChanged} />
        ) : null}
      </div>

      <div className="flex flex-1 overflow-hidden min-h-0">
        <section className="w-[300px] lg:w-[360px] border-r border-outline-variant flex flex-col bg-surface-container-lowest shrink-0">
          <div className="p-3 border-b border-outline-variant space-y-2">
            <Input
              value={listFilter}
              onChange={(e) => setListFilter(e.target.value)}
              placeholder="Filter projects…"
            />
            <Select
              value={leadFilter}
              onChange={(e) => setLeadFilter(e.target.value as LeadFilter)}
              aria-label="Filter projects by lead coverage"
              className="py-1"
            >
              {LEAD_FILTER_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.id === "all" ? "Leads: all projects" : `Leads: ${option.label.toLowerCase()}`} (
                  {leadFilterCounts[option.id] ?? 0})
                </option>
              ))}
            </Select>
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-on-surface-variant">
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={groupByFamily} onChange={(e) => setGroupByFamily(e.target.checked)} />
                Group by family
              </label>
              <label className="flex items-center gap-1.5" title="Projects the family pass marked DUPLICATION">
                <input type="checkbox" checked={hideDuplicates} onChange={(e) => setHideDuplicates(e.target.checked)} />
                Hide duplicates
              </label>
            </div>
            {projects.length < total ? (
              <p className="text-[10px] text-on-surface-variant leading-snug">
                Showing the {projects.length} most recently updated of {total} projects. Set the target filter
                above to one bank to see its projects.
              </p>
            ) : null}
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {loading ? (
              <p className="p-4 text-body-sm text-on-surface-variant">Loading…</p>
            ) : !filtered.length ? (
              <p className="p-4 text-body-sm text-on-surface-variant leading-relaxed">
                {projects.length
                  ? leadFilter !== "all" && !Object.keys(leadSummaries).length
                    ? "Loading lead coverage…"
                    : "No projects match your filters."
                  : "No projects yet. Run PDR synthesis on Vendor Clusters."}
              </p>
            ) : (
              familyGroups ? (
                familyGroups.map((group) => (
                  <div key={group.key || "no-family"}>
                    <p className="sticky top-0 z-10 px-3 py-1.5 text-[10px] uppercase tracking-widest font-data-label text-on-surface-variant bg-surface-container border-b border-outline-variant truncate">
                      {group.label} · {group.rows.length}
                    </p>
                    {group.rows.map(renderRow)}
                  </div>
                ))
              ) : (
                filtered.map(renderRow)
              )
            )}
          </div>
        </section>

        <section className="flex-1 flex flex-col overflow-hidden min-w-0 bg-surface-container-lowest">
          {!selectedId ? (
            <EmptyState
              className="flex-1"
              title="Select a project"
              description="View the full PDR record: narrative fields, inferred values, and reasoning for deal score, risk, department, roles, and family justification."
            >
              <Icon name="folder_open" className="text-5xl text-outline-variant mb-4" />
            </EmptyState>
          ) : detailLoading && !detail ? (
            <div className="flex-1 flex items-center justify-center text-on-surface-variant">
              Loading project…
            </div>
          ) : detail ? (
            <ProjectDetailView
              project={detail}
              leadsRefreshKey={detailRefresh}
              className="flex-1 min-h-0"
              pdrProfile={detail.pdr_schema_profile}
              onChanged={handleProjectChanged}
              onOpenProject={selectProject}
              onLeadsChanged={() => void loadLeadSummaries(projects)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-on-surface-variant">
              Could not load project.
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
